/**
 * Settings Parameter Logger
 *
 * Enable/disable logging by setting SETTINGS_LOGGER_ENABLED in the .env file:
 * - SETTINGS_LOGGER_ENABLED=true  -> Enable logging
 * - SETTINGS_LOGGER_ENABLED=false -> Disable logging
 * - Leave unset -> Log only in development mode (default)
 */
import { useSettings } from '@wix/tpa-settings/react';
import { useParamLogger } from './paramLogger';

interface SettingsParamsObject {
  [key: string]: any;
}

/**
 * Custom hook for logging all settings parameter changes in widget components
 *
 * Environment Control:
 * Add to your .env file:
 * ```
 * SETTINGS_LOGGER_ENABLED=true   # Enable logging
 * SETTINGS_LOGGER_ENABLED=false  # Disable logging
 * # Leave unset for development mode default
 * ```
 *
 * Usage:
 * ```typescript
 * import settingsParams from '../../settingsParams';
 * import { useSettingsParamLogger } from '../utils/settingsParamLogger';
 *
 * export const MyWidgetComponent = () => {
 *   useSettingsParamLogger(settingsParams, 'MyWidget');
 *   // ... rest of component
 * };
 * ```
 *
 * @param settingsParams - The settingsParams object (e.g., from Login/settingsParams.ts)
 */
export const useSettingsParamLogger = (
  settingsParams: SettingsParamsObject,
) => {
  const settings = useSettings();

  useParamLogger(settingsParams, settings.get.bind(settings), {
    envVarName: 'SETTINGS_LOGGER_ENABLED',
    logEmoji: '⚙️',
    paramTypeName: 'settings',
  });
};
